import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useIsClient } from '../useIsClient';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { updateUserCanManageEventPageProperty } from '@/utils/dataLayer';
import { useEffect } from 'react';

export type UserDataResponse = {
  userId: string | null;
  sessionId: string | null;
  guestId: string | null;
  canManageEvent: boolean;
  showAccessLink: boolean;
  hasLikedEvent: boolean;
  countryCode: string | null;
  marketingContact: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
};

export const getUserData = async (eventId: string, isOnlineEvent: boolean): Promise<UserDataResponse | null> => {
  try {
    const url = isOnlineEvent ? `/e/api/${eventId}/me?isOnlineEvent=true` : `/e/api/${eventId}/me`;

    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) return null;
    return (await response.json()) as UserDataResponse;
  } catch {
    return null;
  }
};

export const getUserDataQueryOptions = (
  eventId: string | undefined,
  isOnlineEvent: boolean | undefined,
  enabled: boolean = true,
): UseQueryOptions<UserDataResponse | null> => {
  return {
    queryKey: ['user-data', eventId, isOnlineEvent],
    queryFn: () => {
      if (!eventId) return null;
      return getUserData(eventId, isOnlineEvent ?? false);
    },
    enabled: enabled && !!eventId,
    staleTime: Infinity,
    cacheTime: Infinity,
  };
};

export const useUserData = () => {
  const isClient = useIsClient();
  const { id, isOnline } = useBasicInfo();

  const { data, isLoading } = useQuery(getUserDataQueryOptions(id, isOnline, isClient));

  useEffect(() => {
    if (data) {
      updateUserCanManageEventPageProperty(data.canManageEvent);
    }
  }, [data]);

  return {
    userId: data?.userId ?? null,
    sessionId: data?.sessionId ?? null,
    guestId: data?.guestId ?? null,
    canManageEvent: data?.canManageEvent ?? false,
    showAccessLink: data?.showAccessLink ?? false,
    hasLikedEvent: data?.hasLikedEvent ?? false,
    countryCode: data?.countryCode ?? null,
    marketingContact: data?.marketingContact ?? null,
    isLoading,
  };
};
